import React from 'react';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';
import { Button } from 'dig-components/buttons';
import APIAppSection, { ApiAppProps } from './api-app-section';
import APIAccountCallbackSection from './api-account-callback-section';
import APIKeySection from './api-key-section';
import styles from './api-setting.scss';
import HSFetch, { getCSRFToken } from 'hellospa/hs-fetch';
import { Banner } from 'dig-components/banner';
import { Text } from 'dig-components/typography';
import Spacer, { SpacerSize } from 'hellospa/foundation/spacing/spacer';
import { trackHeapCustomEvent } from 'common/utils/heap';

type APISettingsProps = {
  hasTeam: boolean,
  canUseApi: boolean,
  hasApiAccess: boolean,
  supportEmail: string,
  brandName: string,
  accountCallbackUrl: string,
  createdApps: ApiAppProps[],
  grantedApps: ApiAppProps[],
}

export type AccountCallbackTestResponse = {
  error?: string,
  matched?: boolean,
  message?: string,
}

function APISettings({
  hasTeam,
  hasApiAccess,
  canUseApi,
  supportEmail,
  brandName,
  accountCallbackUrl,
  createdApps,
  grantedApps,
}: APISettingsProps) {
  trackHeapCustomEvent('api_setting_page_view');
  let notice = <FormattedMessage
    id=''
    description='caption text, settings page, tells users that if they wanted access to the api and you need to get a new plan'
    defaultMessage='Production access to the {brand_name} API requires an API plan. {link_document} or {plans_pricing_link}.'
    values={{
      brand_name: <strong>{brandName}</strong>,
      link_document: <a href='/api' target='_blank' rel='noopener noreferrer'>get more
        information</a>,
      plans_pricing_link: <a href='/api/pricing' target='_blank' rel='noopener noreferrer'>see our plans and
        pricing</a>,
    }} />;
  if (hasTeam && !hasApiAccess) {
    notice = <FormattedHTMLMessage
      id=''
      description='warning message, settings page, note to user that role is not the correct access level to perform API requests'
      defaultMessage='<strong>You are currently a non-developer team member.</strong> This means you will be unable
        to make API requests using this account. If you wish to do so, please contact a team administrator who
        can promote your account to "Developer" status.' />;
  } else if (canUseApi) {
    notice = <FormattedMessage
      id=''
      description='caption text, settings page, message for developers to help them build custom software using API'
      defaultMessage='Need help building an integration with our API? Check out our <l2>documentation</l2> for examples or email our <l1>API Support team</l1>.'
      values={{
        /* eslint-disable-next-line react/display-name */
        l1: (...chunks: string[]) => <a href={`mailto:${supportEmail}`} target='_blank' rel='noopener noreferrer'>{chunks}</a>,
        /* eslint-disable-next-line react/display-name */
        l2: (...chunks: string[]) => <a href='/api/documentation' target='_blank' rel='noopener noreferrer'>{chunks}</a>,
      }} />;
  }

  const testAccountCallback = React.useCallback(
    async (url: string): Promise<AccountCallbackTestResponse> => {
      const formData = new FormData();
      formData.append('callback_url', url);
      formData.append('csrf_token', getCSRFToken());

      const response = await HSFetch('/home/testEventCallback', {
        method: 'POST',
        body: formData,
      });

      const json: AccountCallbackTestResponse = await response.json();

      return json;
    },
    [],
  );

  return (
    <div>
      <Spacer top={SpacerSize.XL} />
      <Banner
        variant='basic'
        withLeftIcon={false}
        type='neutral'
      >
        <Banner.Message>
          <span>{notice}</span>
        </Banner.Message>
      </Banner>
      <Spacer top={SpacerSize.SM} />
      <div className='m-settings--section is-api no-margin'>
        <div className='section-header'>
          <div>
            <Text size='large' isBold>
              <FormattedMessage
                id=''
                description='title, settings page, title on the sub section that links to another page which is a dashboard'
                defaultMessage='API dashboard (beta)' />
            </Text>
            <div>
              <Text>
                <Spacer top={SpacerSize.SM} />
                <FormattedMessage
                  id=''
                  description='caption text, settings page, details the section where the dashboard is for the API'
                  defaultMessage='Get detailed info on your API usage, including requests and callback events.' />
              </Text>
            </div>
          </div>

          <div className='api-section-right'>
            <Spacer top={SpacerSize.MD} />
            <Button
              data-qa-ref="api-dashboard-link"
              variant='outline'
              href='/apidashboard'
              target='_blank'
            >
              <FormattedMessage
                id=''
                description='button, settings page, links to a dashboard that shows stats about API usage'
                defaultMessage='View API dashboard' />
            </Button>
          </div>
        </div>
      </div>
      <APIKeySection />
      <div className={styles.accountCallback}>
        <APIAccountCallbackSection onTestSubmit={testAccountCallback} url={accountCallbackUrl} />
      </div>
      <div className={styles.apiApps}>
        <APIAppSection createdApps={createdApps} grantedApps={grantedApps} />
      </div>
    </div>
  );
}

export default APISettings;
